// ============================================================================
// Fulfilment Proofs — the files that evidence a booking actually happened:
// Proof of Pickup (POP), Proof of Delivery (POD) and the Lorry Receipt (LR).
//
// Rendered inside <BookingDetails> so both the Logistics dashboard drawer and
// the OMS order page's leg drawer get it, keyed by the LogisticsDocument id.
//
// Proofs arrive two ways and render identically: a manual operator upload
// (source "manual") and an automatic courier push (source "loginext_epod").
// The only difference the UI draws is a small provenance badge. The LoginExt
// side is a server-side webhook — here we only ever list / upload / delete.
// ============================================================================
import {
  DeleteOutlined,
  FileTextOutlined,
  InboxOutlined,
  ReloadOutlined,
  UploadOutlined,
} from "@ant-design/icons";
import {
  Alert,
  Button,
  Empty,
  Popconfirm,
  Segmented,
  Space,
  Spin,
  Tag,
  Tooltip,
  Typography,
  Upload,
  message,
} from "antd";
import type { UploadFile } from "antd/es/upload/interface";
import dayjs from "dayjs";
import React, { useCallback, useEffect, useRef, useState } from "react";
import {
  deleteProof,
  listProofs,
  uploadProofs,
} from "../logistics/logistics.api";
import { FulfillmentType, LogisticsProof } from "../types";
import { usePermissions } from "../../../custom_hooks/permission/permission_hook";

// The three categories, in the order a shipment moves through them. `key` is the
// canonical fulfillment_type the upload sends; `abbr` is the short capsule label
// (POP / POD / LR); `title` is the full heading shown under the capsules.
const SECTIONS: { key: FulfillmentType; abbr: string; title: string }[] = [
  { key: "pickup", abbr: "POP", title: "Proof of Pickup" },
  { key: "delivery", abbr: "POD", title: "Proof of Delivery" },
  { key: "lr", abbr: "LR", title: "Lorry Receipt" },
];

const IMAGE_EXT = /\.(png|jpe?g|gif|webp|bmp|svg)$/i;

// Best-effort "is this an image?" from the URL path (ignoring any query string),
// so we can show a real thumbnail rather than a generic file icon.
const isImageUrl = (url: string): boolean => {
  const path = url.split(/[?#]/)[0];
  return IMAGE_EXT.test(path);
};

// A readable file name from the stored URL's last path segment.
const fileNameFromUrl = (url: string): string => {
  try {
    const path = decodeURIComponent(new URL(url).pathname);
    return path.split("/").filter(Boolean).pop() || "file";
  } catch {
    return url.split(/[?#]/)[0].split("/").filter(Boolean).pop() || "file";
  }
};

// One proof: a thumbnail/file link that opens in a new tab, its provenance, its
// date, and a delete button.
const ProofCard: React.FC<{
  proof: LogisticsProof;
  onDeleted: () => void;
  // When false the card is read-only (view/open only) — no delete control.
  canAct: boolean;
}> = ({ proof, onDeleted, canAct }) => {
  const [deleting, setDeleting] = useState(false);
  const isAuto = proof.source === "loginext_epod";
  const isImage = isImageUrl(proof.url);

  const remove = async () => {
    setDeleting(true);
    const ok = await deleteProof(proof.id);
    setDeleting(false);
    if (ok) {
      message.success("Proof removed");
      onDeleted();
    }
  };

  return (
    <div
      style={{
        display: "flex",
        gap: 12,
        padding: 8,
        border: "1px solid #f0f0f0",
        borderRadius: 6,
        alignItems: "center",
      }}
    >
      <a
        href={proof.url}
        target="_blank"
        rel="noopener noreferrer"
        title="Open in a new tab"
        style={{
          flex: "0 0 auto",
          width: 56,
          height: 56,
          borderRadius: 4,
          overflow: "hidden",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          background: "#fafafa",
          border: "1px solid #f0f0f0",
        }}
      >
        {isImage ? (
          <img
            src={proof.url}
            alt={fileNameFromUrl(proof.url)}
            style={{ width: "100%", height: "100%", objectFit: "cover" }}
          />
        ) : (
          <FileTextOutlined style={{ fontSize: 24, color: "#8c8c8c" }} />
        )}
      </a>

      <div style={{ flex: 1, minWidth: 0 }}>
        <a
          href={proof.url}
          target="_blank"
          rel="noopener noreferrer"
          style={{
            display: "block",
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
          }}
        >
          {fileNameFromUrl(proof.url)} ↗
        </a>
        <Space size={6} wrap style={{ marginTop: 2 }}>
          {isAuto ? (
            <Tooltip
              title={
                proof.provider_request_ref
                  ? `Courier reference ${proof.provider_request_ref}`
                  : "Pushed automatically by the courier"
              }
            >
              <Tag color="blue" style={{ marginRight: 0 }}>
                Auto (LoginExt)
              </Tag>
            </Tooltip>
          ) : (
            <Tag style={{ marginRight: 0 }}>
              Uploaded by {proof.uploaded_by_name || "operator"}
            </Tag>
          )}
          <Typography.Text type="secondary" style={{ fontSize: 12 }}>
            {dayjs(proof.created_at).format("DD MMM YYYY HH:mm")}
          </Typography.Text>
        </Space>
      </div>

      {canAct && (
        <Popconfirm
          title="Remove this proof?"
          description="It will no longer show on this booking."
          okText="Remove"
          okButtonProps={{ danger: true }}
          cancelText="Keep"
          onConfirm={remove}
        >
          <Button
            size="small"
            type="text"
            danger
            loading={deleting}
            icon={<DeleteOutlined />}
          />
        </Popconfirm>
      )}
    </div>
  );
};

// One fulfilment section: its proofs plus a staged multi-file upload control.
const ProofSection: React.FC<{
  docId: number;
  type: FulfillmentType;
  title: string;
  proofs: LogisticsProof[];
  onChanged: () => void;
  // When false the section is read-only — proofs list without upload/delete controls.
  canAct: boolean;
}> = ({ docId, type, title, proofs, onChanged, canAct }) => {
  const [fileList, setFileList] = useState<UploadFile[]>([]);
  const [uploading, setUploading] = useState(false);
  // Validation copy from a 400 (and the 502 retry line) shown above the picker.
  const [errors, setErrors] = useState<string[]>([]);

  const submit = async () => {
    const files = fileList
      .map((f) => f.originFileObj as File | undefined)
      .filter((f): f is File => !!f);
    if (!files.length) {
      setErrors(["Choose at least one file to upload."]);
      return;
    }
    setUploading(true);
    setErrors([]);
    const res = await uploadProofs(docId, type, files);
    setUploading(false);
    if (res.ok) {
      message.success(
        `Uploaded ${files.length} file${files.length === 1 ? "" : "s"}`
      );
      setFileList([]);
      onChanged();
      return;
    }
    // Flatten field errors + the top-level message into lines for the Alert.
    const lines = [
      ...Object.values(res.fieldErrors ?? {}).flat(),
      ...(res.fieldErrors ? [] : [res.message]),
    ];
    setErrors(lines.length ? lines : [res.message]);
  };

  return (
    <div style={{ marginBottom: 20 }}>
      <Typography.Text strong style={{ display: "block", marginBottom: 8 }}>
        {title}{" "}
        <Typography.Text type="secondary" style={{ fontWeight: 400 }}>
          ({proofs.length})
        </Typography.Text>
      </Typography.Text>

      {proofs.length === 0 ? (
        <Empty
          image={Empty.PRESENTED_IMAGE_SIMPLE}
          description="No files yet"
          style={{ margin: "4px 0 12px" }}
        />
      ) : (
        <Space direction="vertical" size={8} style={{ width: "100%", marginBottom: 12 }}>
          {proofs.map((p) => (
            <ProofCard key={p.id} proof={p} onDeleted={onChanged} canAct={canAct} />
          ))}
        </Space>
      )}

      {canAct && errors.length > 0 && (
        <Alert
          type="error"
          showIcon
          style={{ marginBottom: 8 }}
          message="Could not upload"
          description={
            errors.length === 1 ? (
              errors[0]
            ) : (
              <ul style={{ margin: 0, paddingLeft: 18 }}>
                {errors.map((e, i) => (
                  <li key={i}>{e}</li>
                ))}
              </ul>
            )
          }
        />
      )}

      {canAct && (
        <>
          <Upload
            multiple
            accept="image/*,application/pdf"
            fileList={fileList}
            // Stage files locally; the actual POST is one multipart request on submit.
            beforeUpload={() => false}
            onChange={({ fileList: fl }) => {
              setFileList(fl);
              if (errors.length) setErrors([]);
            }}
            listType="text"
          >
            <Button icon={<InboxOutlined />} size="small">
              Choose files
            </Button>
          </Upload>

          <Button
            type="primary"
            size="small"
            icon={<UploadOutlined />}
            loading={uploading}
            disabled={fileList.length === 0}
            onClick={submit}
            style={{ marginTop: 8 }}
          >
            Upload{fileList.length ? ` (${fileList.length})` : ""}
          </Button>
        </>
      )}
    </div>
  );
};

const FulfilmentProofs: React.FC<{ docId: number; adminOnly?: boolean }> = ({
  docId,
  adminOnly = false,
}) => {
  const { hasPermission } = usePermissions();
  // On the OMS order page (adminOnly) uploads/deletes require oms.update; elsewhere
  // (the Logistics dashboard) the section keeps its existing open behaviour.
  const canAct = !adminOnly || hasPermission("oms.update");
  const [proofs, setProofs] = useState<LogisticsProof[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);
  const [active, setActive] = useState<FulfillmentType>("pickup");
  // Once the operator picks a capsule themselves, stop auto-jumping them around
  // on refresh — but until then, land on whichever category actually has files.
  const userPicked = useRef(false);

  const load = useCallback(async () => {
    setLoading(true);
    const res = await listProofs(docId);
    if (res) {
      setProofs(res.results);
      setError(false);
      if (!userPicked.current) {
        const firstWithFiles = SECTIONS.find((s) =>
          res.results.some((p) => p.proof_type === s.key)
        );
        if (firstWithFiles) setActive(firstWithFiles.key);
      }
    } else {
      setError(true);
    }
    setLoading(false);
  }, [docId]);

  useEffect(() => {
    load();
  }, [load]);

  const grouped = (type: FulfillmentType) =>
    proofs.filter((p) => p.proof_type === type);

  const activeSection = SECTIONS.find((s) => s.key === active) ?? SECTIONS[0];

  return (
    <div style={{ marginTop: 20 }}>
      <Space
        align="center"
        style={{ justifyContent: "space-between", width: "100%", marginBottom: 12 }}
      >
        <Typography.Text strong style={{ fontSize: 15 }}>
          Fulfilment proofs
        </Typography.Text>
        <Tooltip title="Refresh proofs">
          <Button
            size="small"
            type="text"
            icon={<ReloadOutlined />}
            loading={loading}
            onClick={load}
          />
        </Tooltip>
      </Space>

      {error && (
        <Alert
          type="error"
          showIcon
          style={{ marginBottom: 12 }}
          message="Could not load proofs"
          action={
            <Button size="small" danger onClick={load}>
              Try again
            </Button>
          }
        />
      )}

      {/* Capsules: POP / POD / LR — each with its live count, so ops can see at a
          glance which category has files without switching between them. */}
      <Segmented
        block
        value={active}
        onChange={(v) => {
          userPicked.current = true;
          setActive(v as FulfillmentType);
        }}
        style={{ marginBottom: 12 }}
        options={SECTIONS.map((s) => {
          const n = grouped(s.key).length;
          return { value: s.key, label: n ? `${s.abbr} (${n})` : s.abbr };
        })}
      />

      <Spin spinning={loading && proofs.length === 0}>
        <ProofSection
          key={activeSection.key}
          docId={docId}
          type={activeSection.key}
          title={activeSection.title}
          proofs={grouped(activeSection.key)}
          onChanged={load}
          canAct={canAct}
        />
      </Spin>
    </div>
  );
};

export default FulfilmentProofs;
